import React from 'react';
import { useTranslation } from 'react-i18next';
import { Compass, ShieldCheck, Leaf, Map as MapIcon, Users } from 'lucide-react';
import SEO from '../../components/SEO/SEO';
import PageHeader from '../../components/PageHeader/PageHeader';
import TeamCard from '../../components/TeamCard/TeamCard';
import AboutCarousel from '../../components/AboutCarousel/AboutCarousel';
import styles from './About.module.css';

// JSON-LD: ItemList of Person entities for each team member
// Helps LLMs and Google build named entity associations with CINBIOSIS
const TEAM_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  name: 'Equipo Técnico de CINBIOSIS',
  description: 'Especialistas en consultoría ambiental, licenciamiento, sistemas MRV y servicios geoespaciales en Colombia.',
  itemListElement: [
    {
      '@type': 'ListItem',
      position: 1,
      item: {
        '@type': 'Person',
        name: 'Mercedes Álvarez',
        jobTitle: 'Directora de Estrategia Climática y ESG',
        description: 'Especialista en mercados de carbono voluntarios, finanzas climáticas sostenibles y estructuración de proyectos REDD+ bajo estándares Verra VCS y Gold Standard.',
        worksFor: { '@type': 'Organization', name: 'CINBIOSIS', url: 'https://cinbiosis.com' },
        knowsAbout: ['Mercados de carbono', 'REDD+', 'Verra VCS', 'Gold Standard', 'Finanzas sostenibles'],
      },
    },
    {
      '@type': 'ListItem',
      position: 2,
      item: {
        '@type': 'Person',
        name: 'Camila Fernández',
        jobTitle: 'Directora de Proyectos e Infraestructura',
        description: 'Especialista en licenciamiento ambiental ante ANLA, formulación de EIA y PMA, y gestión técnica de grandes corredores viales.',
        worksFor: { '@type': 'Organization', name: 'CINBIOSIS', url: 'https://cinbiosis.com' },
        knowsAbout: ['Licenciamiento ambiental', 'ANLA', 'EIA', 'PMA', 'Infraestructura vial'],
      },
    },
    {
      '@type': 'ListItem',
      position: 3,
      item: {
        '@type': 'Person',
        name: 'Milena Patiño',
        jobTitle: 'Líder de Cumplimiento y Análisis Biótico',
        description: 'Experta en monitoreo de biodiversidad, generación de geodatabases SIG y coordinación de informes ICA ante autoridades ambientales colombianas.',
        worksFor: { '@type': 'Organization', name: 'CINBIOSIS', url: 'https://cinbiosis.com' },
        knowsAbout: ['Monitoreo de biodiversidad', 'SIG', 'ICA', 'Fauna y flora', 'Geodatabases'],
      },
    },
    {
      '@type': 'ListItem',
      position: 4,
      item: {
        '@type': 'Person',
        name: 'Laura López',
        jobTitle: 'Especialista en Estructuración y Analítica de Proyectos',
        description: 'Experta en metodología MGA para proyectos públicos ante SGR, estadística aplicada e indicadores de desempeño ambiental.',
        worksFor: { '@type': 'Organization', name: 'CINBIOSIS', url: 'https://cinbiosis.com' },
        knowsAbout: ['Metodología MGA', 'SGR', 'Estadística ambiental', 'Indicadores de desempeño'],
      },
    },
  ],
};

const About = () => {
  const { t, i18n } = useTranslation();
  const isEn = i18n.language === 'en';

  return (
    <div className={styles.aboutPage}>
      <SEO
        title={t('seo.aboutTitle')}
        description={t('seo.aboutDesc')}
        canonicalPath={`/${isEn ? 'about' : 'nosotros'}`}
        jsonLd={TEAM_SCHEMA}
      />

      <PageHeader
        title={t('about.headerTitle')}
        description={t('about.headerDesc')}
      />

      {/* Who We Are */}
      <section className={styles.whoWeAre}>
        <div className={`container ${styles.twoColLayout}`}>
          <div className={styles.whoWeAreText}>
            <h2>{t('about.whoWeAre')}</h2>
            <div className={styles.prose}>
              {t('about.whoWeAreText').split('\n').map((paragraph, index) => (
                <p key={index}>{paragraph}</p>
              ))}
            </div>
          </div>
          <div className={styles.whoWeAreVisual}>
            <AboutCarousel />
          </div>
        </div>
      </section>

      {/* Corporate Values */}
      <section className={styles.values}>
        <div className="container">
          <div className="text-center mb-4">
            <h2>{t('about.valuesTitle')}</h2>
            <p className="text-muted" style={{ maxWidth: '600px', margin: '0 auto' }}>
              {t('about.valuesSubtitle')}
            </p>
          </div>
          <div className={styles.valuesGrid}>
            <div className={styles.valueCard}>
              <div className={styles.iconWrapper}><Compass size={32} /></div>
              <h3>{t('about.values.v1.title')}</h3>
              <p>{t('about.values.v1.desc')}</p>
            </div>
            <div className={styles.valueCard}>
              <div className={styles.iconWrapper}><ShieldCheck size={32} /></div>
              <h3>{t('about.values.v2.title')}</h3>
              <p>{t('about.values.v2.desc')}</p>
            </div>
            <div className={styles.valueCard}>
              <div className={styles.iconWrapper}><Leaf size={32} /></div>
              <h3>{t('about.values.v3.title')}</h3>
              <p>{t('about.values.v3.desc')}</p>
            </div>
            <div className={styles.valueCard}>
              <div className={styles.iconWrapper}><MapIcon size={32} /></div>
              <h3>{t('about.values.v4.title')}</h3>
              <p>{t('about.values.v4.desc')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Key Team */}
      <section className={styles.team}>
        <div className="container">
          <div className="text-center mb-4">
            <h2>{t('about.teamTitle')}</h2>
            <p className="text-muted">{t('about.teamSubtitle')}</p>
          </div>
          <div className={styles.teamGrid}>
            <TeamCard
              name={t('about.team.camila.name')}
              role={t('about.team.camila.role')}
              desc={t('about.team.camila.desc')}
              imageFilename="camila.jpg"
            />
            <TeamCard
              name={t('about.team.milena.name')}
              role={t('about.team.milena.role')}
              desc={t('about.team.milena.desc')}
              imageFilename="milena.png"
            />
            <TeamCard
              name={t('about.team.mercedes.name')}
              role={t('about.team.mercedes.role')}
              desc={t('about.team.mercedes.desc')}
              imageFilename="mercedes.jpeg"
            />
            <TeamCard
              name={t('about.team.laura.name')}
              role={t('about.team.laura.role')}
              desc={t('about.team.laura.desc')}
              imageFilename={null}
            />
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
